import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { SkeletonMindmap } from '../../features/skeleton'
import { useNodeCreation } from './Nodes/hooks/useNodeCreation'
import { useNodeOperations } from './Nodes/hooks/useNodeOperations'
import { useMindMapLoader } from './hooks/useMindMapLoader'
import { useParams } from 'react-router-dom'
import { type Node, type Edge, type ReactFlowInstance } from '@xyflow/react'
import { App, Result, Button } from 'antd'
import { FileSearchOutlined, WarningOutlined } from '@ant-design/icons'
import { styleEdgesWithHierarchy } from '../../utils'
import { DeleteConfirmationModal } from './Modals/DeleteConfirmationModal'
import { LabelFilterProvider } from '../../features/labelFilter'
import { SaveStateFeature } from './Panels/SaveStateFeature'
import { useSaveQueue } from '../../hooks/useSaveQueue'
import { type NodeActionsContextValue } from '../../context/nodeActions'
import { StakeholderProvider } from '../../features/stakeholder'
import { LabelProvider } from '../../context/label'
import { CharacterModalProvider } from '../../context/CharacterModalContext'
import { BranchSelectionProvider } from '../../features/branchSelection'
import {
  AIStateStoreProvider,
  AICallbacksProvider,
  type AICallbacksContextValue,
} from '../../context/aiGeneration'
import { MindMapProvider } from '../../context/mindmap'
import { useReadOnlyContext } from '../../context/readOnly'
import { useDataAccessContext } from '../../context/dataAccess'
import { SuperpowersProvider } from '../../context/superpowers'
import { ChatWindowProvider } from './Sidebars/ChatWindow/ChatWindowContext'
import { ActiveChatProvider } from '../../context/activeChat'
import { IdeaActionsProvider } from './IdeaActions'
import type { ProjectLabel } from '@bandos/shared'
import type { ReactFlowNodeData } from '../../types/mindmap-components'
import { LoadingContainer, MainLayoutContainer } from './styles/MindMapFlowStyles'
import { MindMapFlowContent } from './MindMapFlowContent'
import { TYPOGRAPHY } from '../../styles/theme'

// Extracted hooks
import { useAIOperations } from './hooks/useAIOperations'
import { useNodeDeletion } from './hooks/useNodeDeletion'
import { useNodeClickHandlers } from './hooks/useNodeClickHandlers'
import { useNodeDragHandlers } from './hooks/useNodeDragHandlers'
import { useEdgeHandlers } from './hooks/useEdgeHandlers'
import { useNodeParenting } from './hooks/useNodeParenting'

export const MindMapFlow = () => {
  const { id: mindmapId } = useParams<{ id: string }>()
  const { notification: antdNotification } = App.useApp()
  const saveQueue = useSaveQueue()

  // ============================================================================
  // REFS
  // ============================================================================

  const reactFlowInstanceRef = useRef<ReactFlowInstance<Node<ReactFlowNodeData>, Edge> | null>(null)
  const reactFlowWrapperRef = useRef<HTMLDivElement>(null)
  const stakeholderCountRef = useRef<number>(0)
  const getNodeLabelsRef = useRef<((nodeId: string) => ProjectLabel[]) | null>(null)

  // Store React Flow instance globally for AI position calculations
  useEffect(() => {
    if (reactFlowInstanceRef.current) {
      ;(
        window as Window &
          typeof globalThis & {
            reactFlowInstance: ReactFlowInstance<Node<ReactFlowNodeData>, Edge> | null
          }
      ).reactFlowInstance = reactFlowInstanceRef.current
    }
    return () => {
      ;(
        window as Window &
          typeof globalThis & {
            reactFlowInstance: ReactFlowInstance<Node<ReactFlowNodeData>, Edge> | null
          }
      ).reactFlowInstance = null
    }
  }, [])

  // Initialize global lastEdgeDropPosition
  useEffect(() => {
    ;(
      window as Window &
        typeof globalThis & { lastEdgeDropPosition: { x: number; y: number } | null }
    ).lastEdgeDropPosition = null
    return () => {
      ;(
        window as Window &
          typeof globalThis & { lastEdgeDropPosition: { x: number; y: number } | null }
      ).lastEdgeDropPosition = null
    }
  }, [])

  // ============================================================================
  // READ-ONLY & DATA ACCESS
  // ============================================================================

  const { isReadOnly, activateReadOnly, deactivateReadOnly } = useReadOnlyContext()
  const { mode: dataAccessMode } = useDataAccessContext()

  // ============================================================================
  // DATA LOADING & STATE
  // ============================================================================

  const {
    nodes,
    edges,
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
    isLoading: isLoadingMindmap,
    notFound,
    loadError,
    hasUnsavedChanges,
    isArchived,
    mindmapTitle,
    mindmapImageStyle,
    setMindmapTitle,
    setMindmapImageStyle,
  } = useMindMapLoader({
    mindmapId,
    antdNotification,
    saveQueue,
    dataAccessMode,
  })

  // Sync archived state into the read-only context
  useEffect(() => {
    if (isArchived) activateReadOnly('archived')
    else deactivateReadOnly('archived')
  }, [isArchived, activateReadOnly, deactivateReadOnly])

  const getCurrentNodes = useCallback((): Node<ReactFlowNodeData>[] => {
    return reactFlowInstanceRef.current?.getNodes() ?? []
  }, [])

  // Component state
  const [autoEditNodeId, setAutoEditNodeId] = useState<string | undefined>()

  // ============================================================================
  // DERIVED MEMOS
  // ============================================================================

  const nodeMap = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes])

  const nodesWithParent = useMemo(() => new Set(edges.map((e) => e.target)), [edges])

  const hierarchyStyledEdges = useMemo(() => {
    return styleEdgesWithHierarchy(edges)
  }, [edges])

  const styledEdges = useMemo(() => {
    return hierarchyStyledEdges.map((edge) => ({
      ...edge,
      data: {
        ...edge.data,
        targetNode: nodeMap.get(edge.target),
      },
    }))
  }, [hierarchyStyledEdges, nodeMap])

  // ============================================================================
  // NODE CREATION
  // ============================================================================

  const {
    executeChildNodeCreation,
    executeRootNodeCreation,
    executeEdgeDropNodeCreation,
    executeParentNodeCreation,
    executeParentDropNodeCreation,
  } = useNodeCreation({
    nodes,
    edges,
    setNodes,
    setEdges,
    mindmapId,
    setAutoEditNodeId,
    reactFlowInstanceRef,
  })

  // Auto-create a venture node when a new (empty) mindmap is first opened.
  const hasAutoCreatedVentureNode = useRef(false)

  useEffect(() => {
    hasAutoCreatedVentureNode.current = false
  }, [mindmapId])

  useEffect(() => {
    if (
      !isLoadingMindmap &&
      !isReadOnly &&
      nodes.length === 0 &&
      !hasAutoCreatedVentureNode.current
    ) {
      hasAutoCreatedVentureNode.current = true
      executeRootNodeCreation('venture')
      if (mindmapTitle) {
        setNodes((prev) =>
          prev.map((node) =>
            node.data.category === 'venture'
              ? { ...node, data: { ...node.data, companyName: mindmapTitle } }
              : node
          )
        )
      }
    }
  }, [isLoadingMindmap, isReadOnly, nodes.length, executeRootNodeCreation, mindmapTitle, setNodes])

  // ============================================================================
  // EXTRACTED HOOKS
  // ============================================================================

  // AI operations (generation, feedback, portraits, suggestions)
  const {
    ai,
    aiStateStoreRef,
    addAIGeneratedNodes,
    generateFeedbackForIdea,
    handleGenerateIdeas,
    handleGenerateNeedsFromVenture,
    handleSynthesizeNeedsFromResearch,
  } = useAIOperations({
    mindmapId,
    nodes,
    edges,
    setNodes,
    setEdges,
    reactFlowInstanceRef,
    saveQueue,
  })

  // Node operations (delete, collapse/expand, update)
  const {
    handleAddChild,
    handleDeleteNode,
    handleCollapseNode,
    handleExpandNode,
    handleUpdateNodeLabel,
    handleUpdateNode,
  } = useNodeOperations({
    setNodes,
    setEdges,
    getCurrentNodes,
    getNodeLabelsRef,
    executeChildNodeCreation,
    onNodeLabelChanged: undefined,
    onNodeDeleted: (nodeId) => {
      if (ai.sourceNodes.has(nodeId)) {
        void ai.dismissSourceNode(nodeId)
      }
      ai.markSuggestionNodeDeleted(nodeId)
    },
  })

  // Node deletion with confirmation modal for cascade delete
  const {
    handleDeleteNodeWithConfirmation,
    confirmDeleteWithChildren,
    cancelDelete,
    pendingDelete,
  } = useNodeDeletion({
    getCurrentNodes,
    edges,
    handleDeleteNode,
  })

  // Node parenting (add parent, root node at viewport center)
  const { handleAddParent, handleAddRootNodeAtViewportCenter } = useNodeParenting({
    reactFlowInstanceRef,
    reactFlowWrapperRef,
    executeParentNodeCreation,
    executeRootNodeCreation,
  })

  // Click handlers (node click, pane click, edge click, nodes change filter)
  const { handleNodeClick, handlePaneClick, handleEdgeClick, handleNodesChange } =
    useNodeClickHandlers({
      setNodes,
      onNodesChange,
    })

  // Drag handlers (drag start, drag with descendant movement, drag stop)
  const { handleNodeDragStart, handleNodeDrag, handleNodeDragStop } = useNodeDragHandlers({
    edges,
    setNodes,
  })

  // Edge handlers (edge changes, validation, connect, connect-end)
  const { handleEdgesChange, isValidConnection, handleConnect, onConnectEnd } = useEdgeHandlers({
    nodes,
    edges,
    nodeMap,
    nodesWithParent,
    setEdges,
    onEdgesChange,
    reactFlowInstanceRef,
    executeEdgeDropNodeCreation,
    executeParentDropNodeCreation,
  })

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  const onInit = useCallback(
    (reactFlowInstance: ReactFlowInstance<Node<ReactFlowNodeData>, Edge>) => {
      reactFlowInstanceRef.current = reactFlowInstance
      ;(
        window as Window &
          typeof globalThis & {
            reactFlowInstance: ReactFlowInstance<Node<ReactFlowNodeData>, Edge> | null
          }
      ).reactFlowInstance = reactFlowInstance
    },
    []
  )

  // ============================================================================
  // CONTEXT VALUES
  // ============================================================================

  const mindMapContextValue = useMemo(
    () => ({
      setNodes,
      setEdges,
      setHasUnsavedChanges: () => {},
      nodes,
      edges,
      mindmapId,
      mindmapTitle,
      setMindmapTitle,
      antdNotification,
      saveQueue,
    }),
    [
      setNodes,
      setEdges,
      nodes,
      edges,
      mindmapId,
      mindmapTitle,
      setMindmapTitle,
      antdNotification,
      saveQueue,
    ]
  )

  const aiCallbacksContextValue: AICallbacksContextValue = useMemo(
    () => ({
      dismissSourceNode: ai.dismissSourceNode,
      toggleSuggestion: ai.toggleSuggestion,
      getSuggestionsForNode: ai.getSuggestionsForNode,
      addChatSuggestions: ai.addChatSuggestions,
      getFeedbackHistory: ai.getFeedbackHistory,
    }),
    [
      ai.dismissSourceNode,
      ai.toggleSuggestion,
      ai.getSuggestionsForNode,
      ai.addChatSuggestions,
      ai.getFeedbackHistory,
    ]
  )

  const clearAutoEdit = useCallback(() => setAutoEditNodeId(undefined), [])

  const nodeActionsContextValue: NodeActionsContextValue = useMemo(
    () => ({
      handleDeleteNode: handleDeleteNodeWithConfirmation,
      handleUpdateNodeLabel,
      handleCollapseNode,
      handleExpandNode,
      handleAddChild,
      handleDirectChildCreation: executeChildNodeCreation,
      handleAddParent,
      handleUpdateNode,
      addAIGeneratedNodes,
      handleGenerateIdeas,
      handleGenerateNeedsFromVenture,
      handleSynthesizeNeedsFromResearch,
      handleGenerateRatings: generateFeedbackForIdea,
      autoEditNodeId,
      clearAutoEdit,
    }),
    [
      handleDeleteNodeWithConfirmation,
      handleUpdateNodeLabel,
      handleCollapseNode,
      handleExpandNode,
      handleAddChild,
      executeChildNodeCreation,
      handleAddParent,
      handleUpdateNode,
      addAIGeneratedNodes,
      handleGenerateIdeas,
      handleGenerateNeedsFromVenture,
      handleSynthesizeNeedsFromResearch,
      generateFeedbackForIdea,
      autoEditNodeId,
      clearAutoEdit,
    ]
  )

  // ============================================================================
  // RENDER
  // ============================================================================

  if (notFound) {
    return (
      <LoadingContainer>
        <Result
          icon={<FileSearchOutlined style={{ fontSize: TYPOGRAPHY.h1.fontSize, color: '#999' }} />}
          title="404"
          subTitle="The project you're looking for doesn't exist."
          extra={[
            <Button type="primary" key="home" onClick={() => (window.location.href = '/app/')}>
              Go to Dashboard
            </Button>,
            <Button key="back" onClick={() => window.history.back()}>
              Go Back
            </Button>,
          ]}
        />
      </LoadingContainer>
    )
  }

  if (loadError) {
    return (
      <LoadingContainer>
        <Result
          icon={<WarningOutlined style={{ fontSize: TYPOGRAPHY.h1.fontSize, color: '#faad14' }} />}
          title="Failed to Load Project"
          subTitle={loadError}
          extra={[
            <Button type="primary" key="home" onClick={() => (window.location.href = '/app/')}>
              Go to Dashboard
            </Button>,
            <Button key="retry" onClick={() => window.location.reload()}>
              Try Again
            </Button>,
          ]}
        />
      </LoadingContainer>
    )
  }

  if (isLoadingMindmap) {
    return <SkeletonMindmap />
  }

  return (
    <SuperpowersProvider>
      <StakeholderProvider>
        <LabelProvider>
          <AIStateStoreProvider store={aiStateStoreRef.current}>
            <AICallbacksProvider value={aiCallbacksContextValue}>
              <LabelFilterProvider>
                <SaveStateFeature
                  isSaving={hasUnsavedChanges || saveQueue.saveState.isSaving}
                  lastSaved={saveQueue.saveState.lastSaved}
                  hasUnsavedChanges={hasUnsavedChanges}
                >
                  <MindMapProvider value={mindMapContextValue}>
                    <IdeaActionsProvider>
                      <ActiveChatProvider>
                        <ChatWindowProvider onAddNodes={addAIGeneratedNodes}>
                          <CharacterModalProvider>
                            <BranchSelectionProvider edges={styledEdges} nodes={nodes}>
                              <MainLayoutContainer>
                                <MindMapFlowContent
                                  reactFlowWrapperRef={reactFlowWrapperRef}
                                  getNodeLabelsRef={getNodeLabelsRef}
                                  nodeActionsContextValue={nodeActionsContextValue}
                                  mindmapId={mindmapId}
                                  mindmapTitle={mindmapTitle}
                                  mindmapImageStyle={mindmapImageStyle}
                                  onMindmapTitleChange={setMindmapTitle}
                                  onMindmapImageStyleChange={setMindmapImageStyle}
                                  nodes={nodes}
                                  styledEdges={styledEdges}
                                  reactFlowHandlers={{
                                    handleNodesChange,
                                    handleEdgesChange,
                                    handleConnect,
                                    isValidConnection,
                                    onConnectEnd,
                                    handleNodeClick,
                                    handleNodeDragStart,
                                    handleNodeDrag,
                                    handleNodeDragStop,
                                    handleEdgeClick,
                                    handlePaneClick,
                                    onInit,
                                  }}
                                  onAddRootNode={handleAddRootNodeAtViewportCenter}
                                  stakeholderCountRef={stakeholderCountRef}
                                />
                              </MainLayoutContainer>
                            </BranchSelectionProvider>
                          </CharacterModalProvider>
                        </ChatWindowProvider>
                      </ActiveChatProvider>
                    </IdeaActionsProvider>
                  </MindMapProvider>
                </SaveStateFeature>
              </LabelFilterProvider>
            </AICallbacksProvider>
          </AIStateStoreProvider>
        </LabelProvider>
      </StakeholderProvider>

      {/* Delete confirmation modal - shown when deleting a node with children */}
      <DeleteConfirmationModal
        isVisible={pendingDelete !== null}
        nodeLabel={pendingDelete?.nodeLabel ?? ''}
        descendants={pendingDelete?.descendants ?? []}
        onConfirm={confirmDeleteWithChildren}
        onCancel={cancelDelete}
      />
    </SuperpowersProvider>
  )
}
